import React, { useState, useEffect, useRef } from 'react'
import { motion, useInView } from 'framer-motion'

interface CountUpNumberProps {
  end: string
  duration?: number
  className?: string
}

const CountUpNumber: React.FC<CountUpNumberProps> = ({ 
  end, 
  duration = 2000, 
  className = "" 
}) => {
  const [count, setCount] = useState(0)
  const [suffix, setSuffix] = useState('')
  const [hasAnimated, setHasAnimated] = useState(false)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })

  // Parse the end value to extract number and suffix
  const parseEndValue = (endValue: string) => {
    // Handle different formats
    if (endValue.includes('%')) {
      const num = parseFloat(endValue.replace('%', ''))
      return { number: num, suffix: '%' }
    } else if (endValue.includes('K+')) {
      const num = parseFloat(endValue.replace('K+', ''))
      return { number: num, suffix: 'K+' }
    } else if (endValue.includes('/')) {
      // Handle cases like "24/7"
      return { number: 0, suffix: endValue, isSpecial: true }
    } else {
      const num = parseFloat(endValue.replace(/[^\d.]/g, ''))
      const suffixMatch = endValue.match(/[^\d.]+/)
      return { number: num, suffix: suffixMatch ? suffixMatch[0] : '' }
    }
  }

  useEffect(() => {
    if (isInView && !hasAnimated) {
      const { number, suffix: parsedSuffix, isSpecial } = parseEndValue(end)
      setSuffix(parsedSuffix)
      
      if (isSpecial) {
        // For special cases like "24/7", just show the text directly
        setHasAnimated(true)
        return
      }

      setHasAnimated(true)
      let startTimestamp: number | null = null
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp
        const progress = Math.min((timestamp - startTimestamp) / duration, 1)
        
        // Use easeOutQuart for smooth deceleration
        const easedProgress = 1 - Math.pow(1 - progress, 4)
        const currentCount = easedProgress * number
        
        setCount(currentCount)
        
        if (progress < 1) {
          requestAnimationFrame(step)
        } else {
          setCount(number)
        }
      }
      requestAnimationFrame(step)
    }
  }, [isInView, end, duration, hasAnimated])

  const formatNumber = (num: number) => {
    if (suffix === '%') {
      return num.toFixed(1)
    } else if (suffix === 'K+') {
      return Math.floor(num).toString()
    } else {
      return Math.floor(num).toString()
    }
  }

  const displayValue = () => {
    if (suffix === '24/7') {
      return end // Show "24/7" directly for special cases
    }
    return formatNumber(count) + suffix
  }

  return (
    <motion.span 
      ref={ref}
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: isInView ? 1 : 0 }}
      transition={{ duration: 0.5 }}
    >
      {displayValue()}
    </motion.span>
  )
}

export default CountUpNumber 